import _ from 'lodash'
import { Star } from 'lucide-react'
import { Becy } from '@/components/ui/icon'
import { CasinoGameSection } from '@/components/casino/lobby/CasinoGameSection'
import { CasinoProvidersSection } from '@/components/casino/lobby/CasinoProvidersSection'
import { CasinoGameCardList } from '@/components/casino/lobby/game-card/CasinoGameCardList'
import { OriginalGameCard } from '@/components/original/OriginalGameCard'
import { OriginalGameCardSkeleton } from '@/components/original/OriginalGameCardSkeleton'
import { HorizontalCarouselNav } from '@/components/casino/lobby/carousel/HorizontalCarouselNav'
import { useHorizontalCarousel } from '@/hooks/use-horizontal-carousel'
import { CASINO_TYPE_ORIGINALS, CASINO_TYPE_POPULAR, CASINO_SKELETON_CARD_COUNT } from '@/config/casino'
import { getSortedOriginalGames } from '@/lib/original-games'
import { useLobbyCasinoGames } from '@/hooks/use-lobby-casino-games'
import { useGetLobbyCasinoTypesQuery } from '@/store/api/casinoApi'
import { useGetOriginalGamesQuery } from '@/store/api/originalApi'
import { useAppDispatch } from '@/store/hooks'
import { setSelectedCasinoType } from '@/store/slices/casinoSlice'
import { CasinoLobbyTypeSection } from '@/components/casino/lobby/content/CasinoLobbyTypeSection'

export function CasinoLobbyContent() {
	const dispatch = useAppDispatch()
	const {
		games: popularGames,
		isLoading: isPopularLoading,
		isLoadingMore: isPopularLoadingMore,
		isError: isPopularError,
		hasMoreGames: hasMorePopularGames,
		loadMore: loadMorePopularGames,
	} = useLobbyCasinoGames({ is_popular: true })
	const { data: originalsResponse, isLoading: isOriginalsLoading, isError: isOriginalsError } = useGetOriginalGamesQuery()
	const { data: lobbyTypesResponse, isLoading: isLobbyTypesLoading, isError: isLobbyTypesError } = useGetLobbyCasinoTypesQuery()

	const originalGames = getSortedOriginalGames(originalsResponse?.data ?? [])
	const lobbyTypes = lobbyTypesResponse?.data ?? []
	const hasOriginalsCarousel = !isOriginalsLoading && !isOriginalsError && !_.isEmpty(originalGames)
	const {
		scrollRef: originalsScrollRef,
		canScrollPrev: canScrollOriginalsPrev,
		canScrollNext: canScrollOriginalsNext,
		handleScroll: handleOriginalsScroll,
	} = useHorizontalCarousel(originalGames)

	return (
		<div className='flex flex-col gap-8'>
			<CasinoGameSection
				title='Popular'
				icon={<Star className='h-7 w-7 text-subtle-foreground' />}
				games={popularGames}
				isLoading={isPopularLoading}
				isLoadingMore={isPopularLoadingMore}
				isError={isPopularError}
				hasMoreGames={hasMorePopularGames}
				onLoadMore={loadMorePopularGames}
				onViewAll={() => dispatch(setSelectedCasinoType(CASINO_TYPE_POPULAR))}
			/>

			<section className='flex flex-col gap-4'>
				<div className='flex items-center justify-between gap-4'>
					<div className='flex items-center gap-3'>
						<Becy className='h-7 w-7 text-subtle-foreground' />
						<h2 className='text-2xl font-semibold tracking-[-0.12px] text-foreground'>BetsCycle Original</h2>
					</div>
					<div className='flex items-center gap-3'>
						<button
							type='button'
							onClick={() => dispatch(setSelectedCasinoType(CASINO_TYPE_ORIGINALS))}
							className='text-sm font-medium tracking-[-0.21px] text-muted-foreground transition-colors hover:text-foreground'
						>
							View all
						</button>
						{hasOriginalsCarousel ? (
							<HorizontalCarouselNav canScrollPrev={canScrollOriginalsPrev} canScrollNext={canScrollOriginalsNext} onScroll={handleOriginalsScroll} />
						) : null}
					</div>
				</div>

				{isOriginalsLoading ? (
					<div className='flex gap-6 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'>
						{_.times(CASINO_SKELETON_CARD_COUNT, (index) => (
							<OriginalGameCardSkeleton key={`lobby-original-skeleton-${index}`} />
						))}
					</div>
				) : null}

				{isOriginalsError ? <p className='text-sm text-muted-foreground'>Unable to load original games right now.</p> : null}

				{!isOriginalsLoading && !isOriginalsError && _.isEmpty(originalGames) ? (
					<p className='text-sm text-muted-foreground'>No original games are available right now.</p>
				) : null}

				{!isOriginalsLoading && !isOriginalsError && !_.isEmpty(originalGames) ? (
					<div
						ref={originalsScrollRef}
						className='flex gap-6 overflow-x-auto overflow-y-visible scroll-smooth px-[5px] pb-8 pt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
					>
						{_.map(originalGames, (game) => (
							<OriginalGameCard key={game.id} game={game} />
						))}
					</div>
				) : null}
			</section>

			{isLobbyTypesLoading ? <CasinoGameCardList games={[]} isLoading layout='carousel' skeletonCount={CASINO_SKELETON_CARD_COUNT} /> : null}

			{isLobbyTypesError ? <p className='text-sm text-muted-foreground'>Unable to load casino types right now.</p> : null}

			{!isLobbyTypesLoading && !isLobbyTypesError ? _.map(lobbyTypes, (type) => <CasinoLobbyTypeSection key={type.id} type={type} />) : null}

			<CasinoProvidersSection />
		</div>
	)
}
